/**
 * QuestionnaireForm — 2-step profile-completion wizard (v2, May 2026).
 *
 * Server-driven: the questionnaire structure (Q1 customer type field +
 * per-branch field lists) is fetched from
 *   GET /api/v1/customer/profile/questionnaire-config
 * via `useQuestionnaireConfig()`. There is no client-side fallback —
 * if the fetch fails, the user sees a recoverable error state.
 *
 *   Step 1 — CustomerTypeStep     (Q1 from config)
 *   Step 2 — FinancialDetailsStep (active branch from config)
 *
 * Identity capture (PAN/DOB) was removed from onboarding in the v2 spec
 * and now happens at service-creation time when those values are missing
 * from the customer profile — see `IntakeForm`.
 *
 * On success → invalidates profile cache → navigates to /dashboard.
 */
import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm, type Path } from 'react-hook-form';
import {
  ChevronRight, ChevronLeft, CheckCircle2, Loader2, AlertCircle, RefreshCw, Sparkles, X,
} from 'lucide-react';
import { toast } from '@finmin/ui';
import { useCompleteProfile, useProfile, type ProfileCompleteRequest } from '@/api/profile';
import { questionnaireSchema, type QuestionnaireInput } from './schema';
import { PALETTE_FOREST, PALETTE_AMBER } from './tokens';
import { useQuestionnaireConfig } from './hooks/use-questionnaire-config';
import { QuestionnaireStepper }   from './components/questionnaire-stepper';
import { CustomerTypeStep }       from './components/customer-type-step';
import { FinancialDetailsStep }   from './components/financial-details-step';
// VerificationStep removed: v2 onboarding no longer collects PAN/DOB/ERI.

const TOTAL_STEPS = 2;

export function QuestionnaireForm() {
  const navigate = useNavigate();
  const [step, setStep]                 = React.useState(1);
  const [reonboardDismissed, setReonboardDismissed] = React.useState(false);

  // Existing profile — used to detect "this is a re-onboarding" (the user
  // already had a PAN on file before the May-2026 questionnaire reset).
  const { data: existingProfile } = useProfile();
  const isReOnboarding = Boolean(existingProfile?.pan) && !reonboardDismissed;

  // ── Server-driven config — no fallback. ─────────────────────────────────
  const {
    data: config,
    isLoading: configLoading,
    isError:   configError,
    error:     configFetchError,
    refetch:   refetchConfig,
    isRefetching,
  } = useQuestionnaireConfig();

  const { mutate: completeProfile, isPending } = useCompleteProfile({
    onSuccess: () => {
      toast.success('Profile complete! Welcome to FinminOS.');
      void navigate({ to: '/dashboard', replace: true });
    },
    onError: (err) => {
      toast.error(err.message ?? 'Something went wrong. Please try again.');
    },
  });

  // NOTE: we intentionally do NOT pass `resolver: zodResolver(...)` here.
  //
  // Why: zodResolver runs the ENTIRE schema (including all superRefine
  // checks) on every validation pass. That means even a single
  // `trigger(['customer_type'])` (or RHF running validation internally
  // during HMR re-mounts) would populate formState.errors with messages
  // for Step-2 fields, making Step 2 look pre-broken the moment the user
  // lands on it.
  //
  // Instead we run `questionnaireSchema.safeParse(getValues())` manually
  // ONLY when the user clicks Continue or Complete-my-profile, and call
  // setError ourselves for each issue. This guarantees formState.errors
  // is empty until the user has actually tried to advance.
  const {
    control,
    register,
    watch,
    getValues,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<QuestionnaireInput>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      income_exposures:  [],
      services_required: [],
    },
  });

  const customerType = watch('customer_type');

  // Defensive: wipe any leftover errors on mount (handles Vite HMR where
  // component state — including formState.errors — survives a hot reload).
  React.useEffect(() => {
    clearErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Backend renamed `customer_type_field` → `root_field` in May 2026.
  // Read whichever is present so we don't shatter on either backend version.
  const rootField = config?.root_field ?? config?.customer_type_field ?? null;

  // The branch discriminator may be any of `customer_type`, `key`, `value`,
  // or `name` depending on backend version. Match on whichever is present.
  const activeBranch =
    config && customerType
      ? config.branches.find((b) => {
          const discriminator = b.customer_type ?? b.key ?? b.value ?? b.name;
          return discriminator === customerType;
        }) ?? null
      : null;

  /**
   * Run our schema against the current form values and copy every issue
   * into formState via setError. Returns true when the schema passes.
   *
   * `pathFilter` lets us scope which errors get surfaced — used by Step 1
   * to only set the customer_type error without leaking Step-2 errors into
   * formState yet.
   */
  function validateAndSetErrors(pathFilter?: (path: string) => boolean): boolean {
    clearErrors();
    const parsed = questionnaireSchema.safeParse(getValues());
    if (parsed.success) return true;

    for (const issue of parsed.error.issues) {
      const path = String(issue.path[0] ?? '');
      if (!path) continue;
      if (pathFilter && !pathFilter(path)) continue;
      setError(path as Path<QuestionnaireInput>, {
        type:    issue.code ?? 'custom',
        message: issue.message,
      });
    }
    return false;
  }

  async function goNext() {
    if (step === 1) {
      // Only check customer_type — don't leak Step-2 errors into formState.
      const ok = validateAndSetErrors((p) => p === 'customer_type');
      // ok will be false if other Step-2 issues exist, but we filtered them
      // out — so re-check just the customer_type value directly.
      const ct = getValues('customer_type');
      if (!ct) return;       // setError was already called above
      clearErrors();         // wipe customer_type error (if any) before advancing
      void ok;
      setStep(2);
      return;
    }

    // Step 2 — validate everything.
    const passed = validateAndSetErrors();
    if (!passed) return;
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  }

  function goBack() {
    clearErrors();
    setStep((s) => Math.max(s - 1, 1));
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  /**
   * Builds the API payload from the validated form data.
   *
   * Each customer_type ships only the fields the API expects for that
   * branch — the server's "must not send" rules are honoured by simply
   * not assigning those keys to `payload`.
   */
  function onSubmit(data: QuestionnaireInput) {
    // v2 spec: /profile/complete is questionnaire-only. PAN/DOB/ERI consent /
    // customer_password were removed — collected at service-creation time
    // instead, see IntakeForm.
    const payload: QuestionnaireInput = {
      customer_type: data.customer_type,
    };

    if (data.customer_type === 'salaried') {
      payload.annual_income_range = data.annual_income_range;
      payload.income_exposures    = data.income_exposures ?? [];
      payload.services_required   = data.services_required ?? [];
      if (
        data.income_exposures?.includes('any_other') &&
        data.other_exposure_detail?.trim()
      ) {
        payload.other_exposure_detail = data.other_exposure_detail.trim();
      }
      if (
        data.services_required?.includes('any_other') &&
        data.service_other_detail?.trim()
      ) {
        payload.service_other_detail = data.service_other_detail.trim();
      }
    }

    if (data.customer_type === 'professional') {
      payload.entity_structure    = data.entity_structure;
      payload.annual_income_range = data.annual_income_range;
      payload.services_required   = data.services_required ?? [];
      if (
        data.services_required?.includes('any_other') &&
        data.service_other_detail?.trim()
      ) {
        payload.service_other_detail = data.service_other_detail.trim();
      }
    }

    if (data.customer_type === 'business_owner') {
      payload.entity_structure      = data.entity_structure;
      payload.annual_turnover_range = data.annual_turnover_range;
      payload.services_required     = data.services_required ?? [];
      if (
        data.entity_structure === 'other' &&
        data.entity_structure_other_detail?.trim()
      ) {
        payload.entity_structure_other_detail = data.entity_structure_other_detail.trim();
      }
      if (
        data.services_required?.includes('any_other') &&
        data.service_other_detail?.trim()
      ) {
        payload.service_other_detail = data.service_other_detail.trim();
      }
    }

    if (data.customer_type === 'others') {
      payload.other_customer_category = data.other_customer_category;
      payload.services_required       = data.services_required ?? [];
      if (
        data.other_customer_category === 'other' &&
        data.other_customer_category_detail?.trim()
      ) {
        payload.other_customer_category_detail = data.other_customer_category_detail.trim();
      }
      if (
        data.services_required?.includes('any_other') &&
        data.service_other_detail?.trim()
      ) {
        payload.service_other_detail = data.service_other_detail.trim();
      }
    }

    completeProfile(payload as unknown as ProfileCompleteRequest);
  }

  /**
   * Manual submit handler.
   *
   * Validates the whole schema ourselves so we always give the user feedback
   * — if RHF were doing the validation and an error existed on a field that
   * isn't currently rendered (different step), the submit would silently
   * fail.
   */
  /**
   * Submit handler — invoked ONLY by the explicit Complete-my-profile button.
   *
   * IMPORTANT: this is NOT wired to `<form onSubmit>` and the button below is
   * `type="button"` (not `submit`). The reason: when the user clicks Continue
   * on Step 1 and we call setStep(2), React re-renders synchronously and
   * replaces the Continue button with what WAS the submit button at the same
   * x,y. The browser's mouseup then lands on the new submit button and fires
   * an unwanted submit, populating every Step-2 error before the user has
   * touched anything. Routing submission through an explicit onClick avoids
   * that race entirely.
   */
  async function handleManualSubmit() {
    if (isPending) return;

    const parsed = questionnaireSchema.safeParse(getValues());
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      const path = String(firstIssue?.path[0] ?? '');

      // Step 1 = customer_type, Step 2 = everything else.
      const targetStep = path === 'customer_type' ? 1 : 2;
      if (targetStep !== step) setStep(targetStep);

      // Surface errors into formState now that the user has actually tried.
      validateAndSetErrors();
      toast.error(firstIssue?.message ?? 'Please fix the highlighted fields and try again.');
      return;
    }

    onSubmit(parsed.data);
  }

  // ── Loading state ────────────────────────────────────────────────────────
  if (configLoading) {
    return (
      <div className="w-full max-w-lg mx-auto py-16 flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2
          className="h-6 w-6 animate-spin"
          style={{ color: PALETTE_FOREST }}
          aria-hidden
        />
        <p className="text-sm">Loading the questionnaire…</p>
      </div>
    );
  }

  // ── Error state — recoverable via retry ──────────────────────────────────
  if (configError || !config) {
    return (
      <div className="w-full max-w-lg mx-auto py-12 text-center">
        <div
          className="inline-flex h-12 w-12 rounded-full items-center justify-center mb-4"
          style={{ backgroundColor: `hsl(var(--destructive) / 0.10)` }}
        >
          <AlertCircle className="h-6 w-6 text-destructive" aria-hidden />
        </div>
        <h3 className="font-display font-semibold text-lg text-foreground mb-1">
          We couldn't load the questionnaire
        </h3>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto mb-5">
          {configFetchError?.message ?? 'Please check your connection and try again.'}
        </p>
        <button
          type="button"
          onClick={() => void refetchConfig()}
          disabled={isRefetching}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
          style={{ backgroundColor: PALETTE_FOREST }}
        >
          <RefreshCw
            className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`}
            aria-hidden
          />
          {isRefetching ? 'Retrying…' : 'Try again'}
        </button>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full max-w-lg mx-auto">
      {isReOnboarding && (
        <div
          className="mb-5 rounded-xl border p-4 flex items-start gap-3"
          style={{
            backgroundColor: 'hsl(var(--palette-mint, 152 41% 92%))',
            borderColor:     'hsl(var(--palette-forest, 176 72% 17%) / 0.25)',
          }}
        >
          <Sparkles
            className="h-5 w-5 shrink-0 mt-0.5"
            style={{ color: PALETTE_FOREST }}
            aria-hidden
          />
          <div className="flex-1 text-sm">
            <p className="font-medium text-foreground">We've updated our onboarding</p>
            <p className="text-muted-foreground mt-0.5">
              Please confirm a few details so we can keep filing your taxes accurately
              against the latest tax rules.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setReonboardDismissed(true)}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      <QuestionnaireStepper currentStep={step} />

      {/*
        We intentionally do NOT pass onSubmit here. See `handleManualSubmit`
        comment for why — submission is routed through an explicit onClick on
        the Complete-my-profile button to avoid a Continue-click → mouseup-on-
        new-submit-button race.
      */}
      <form onSubmit={(e) => e.preventDefault()} noValidate>
        <div className="rounded-2xl border border-border bg-background shadow-sm p-6 sm:p-8">

          {step === 1 && rootField && (
            <CustomerTypeStep
              field={rootField}
              control={control}
              error={errors.customer_type?.message}
            />
          )}
          {step === 1 && !rootField && (
            <div className="py-4 text-sm text-muted-foreground">
              The questionnaire configuration is missing a root field. Please
              refresh or contact support.
            </div>
          )}

          {step === 2 && (
            <FinancialDetailsStep
              branch={activeBranch}
              control={control}
              register={register}
              watch={watch}
              errors={errors}
            />
          )}

          {/* Navigation buttons */}
          <div className={`mt-8 flex gap-3 ${step > 1 ? 'justify-between' : 'justify-end'}`}>
            {step > 1 && (
              <button
                type="button"
                onClick={goBack}
                disabled={isPending}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground hover:bg-muted transition-colors disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                Back
              </button>
            )}

            {step < TOTAL_STEPS ? (
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors"
                style={{ backgroundColor: PALETTE_FOREST }}
              >
                Continue
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
            ) : (
              <button
                // Explicitly type="button" + onClick — see handleManualSubmit
                // comment for why this is NOT type="submit".
                type="button"
                onClick={handleManualSubmit}
                disabled={isPending}
                className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                style={{ backgroundColor: isPending ? 'hsl(var(--muted))' : PALETTE_AMBER }}
              >
                {isPending ? 'Saving…' : 'Complete my profile'}
                {!isPending && <CheckCircle2 className="h-4 w-4" aria-hidden />}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
